import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './Button';
import Input, { Select } from './Input';
import { SUPPLEMENTARY_TAX_OPTIONS, getSupplementaryTaxTotal } from '../utils/supplementaryTax';
import { useTarifConfig, mapTsOptionsForSelect } from '../hooks/useTarifConfig';

const LoadSupplementaryTaxModal = ({ isOpen, onClose, onConfirm, loading = false }) => {
  const { t } = useTranslation();
  const [optionCode, setOptionCode] = useState('');
  const [hours, setHours] = useState('0');
  const [errors, setErrors] = useState({});
  const { tarifConfig } = useTarifConfig();

  // Use server-configured TS options if available, else fall back to static defaults
  const resolvedTsOptions = tarifConfig?.tsOptions || null;
  const selectOptions = useMemo(() => {
    const serverMapped = mapTsOptionsForSelect(resolvedTsOptions);
    if (serverMapped) return serverMapped;
    return SUPPLEMENTARY_TAX_OPTIONS.map((option) => ({
      value: option.value,
      label: `${t(option.labelKey)} (${option.hourlyNetAmount.toLocaleString('fr-FR')} FCFA/h)`
    }));
  }, [resolvedTsOptions, t]);

  useEffect(() => {
    if (isOpen) {
      setOptionCode('');
      setHours('0');
      setErrors({});
    }
  }, [isOpen]);

  const parsedHours = Number(String(hours ?? '').trim().replace(',', '.'));
  const totalAmount = useMemo(
    () => getSupplementaryTaxTotal(optionCode, parsedHours, resolvedTsOptions),
    [optionCode, parsedHours, resolvedTsOptions]
  );

  const optionLabel = useMemo(() => {
    const sel = selectOptions.find((o) => o.value === optionCode);
    return sel ? sel.label.split(' (')[0] : '';
  }, [optionCode, selectOptions]);

  const handleConfirm = () => {
    const nextErrors = {};
    if (!optionCode && parsedHours > 0) {
      nextErrors.optionCode = t('loads.ts.validation.optionRequired');
    }

    if (!Number.isFinite(parsedHours) || parsedHours < 0) {
      nextErrors.hours = t('loads.ts.validation.hoursInvalid');
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onConfirm({
      tsOptionCode: optionCode,
      tsHours: parsedHours
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('loads.ts.modalTitle')}
      size="md"
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            {t('loads.ts.confirm')}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{t('loads.ts.modalDescription')}</p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
          <Select
            className="min-w-0 flex-1 w-full"
            label={t('loads.ts.optionLabel')}
            value={optionCode}
            onChange={(e) => setOptionCode(e.target.value)}
            options={selectOptions}
            placeholder={t('loads.ts.optionPlaceholder')}
            error={errors.optionCode}
          />

          <Input
            type="number"
            min="0"
            step="1"
            className="w-full sm:w-44 shrink-0"
            label={t('loads.ts.hoursLabel')}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            error={errors.hours}
          />
        </div>

        {optionCode && Number.isFinite(parsedHours) && parsedHours >= 0 && (
          <div className="rounded-md border border-blue-100 bg-blue-50 p-3">
            <p className="text-sm text-blue-900">
              {t('loads.ts.preview', {
                option: optionLabel,
                hours: parsedHours,
                total: totalAmount.toLocaleString('fr-FR')
              })}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LoadSupplementaryTaxModal;
